import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useEmployees } from '../hooks/useUniverseApi';
import { useAuthStore } from '@shared/store/authStore';

const bookingSchema = z.object({
  serviceId: z.number(),
  employeeId: z.number().optional(),
  date: z.string(),
  timeSlot: z.string(),
  duration: z.number(),
  notes: z.string().optional(),
});

type BookingForm = z.infer<typeof bookingSchema>;

// Real employees from Universe API (same as HomePage)
const realEmployees = [
  { Id: 16, first_name: "Мария", last_name: "Зарицкая", spec_name: "Тренер Тренажерного зала", department: { dep_name: "Тренажерный зал" }, services: [
    { service_id: 1001, service_name: "Персональная тренировка", service_price: 2500, duration: 60, is_group: 0 },
    { service_id: 1002, service_name: "СПЛИТ тренировка", service_price: 3000, duration: 60, is_group: 0 },
    { service_id: 1003, service_name: "Групповая тренировка", service_price: 800, duration: 45, is_group: 1 }
  ]},
  { Id: 20, first_name: "Татьяна", last_name: "Ягармина", spec_name: "Визажист-стилист, бровист", department: { dep_name: "LEVEL Beauty" }, services: [
    { service_id: 2001, service_name: "Макияж на мероприятие", service_price: 3500, duration: 90, is_group: 0 },
    { service_id: 2002, service_name: "Коррекция + окрашивание бровей", service_price: 1000, duration: 30, is_group: 0 },
    { service_id: 2003, service_name: "Ламинирование бровей", service_price: 1500, duration: 60, is_group: 0 }
  ]},
  { Id: 21, first_name: "Александр", last_name: "Петров", spec_name: "Тренер падел", department: {}, services: [
    { service_id: 3001, service_name: "Урок падел тенниса (1 час)", service_price: 2200, duration: 60, is_group: 0 },
    { service_id: 3002, service_name: "Урок падел тенниса (1,5 часа)", service_price: 3300, duration: 90, is_group: 0 }
  ]},
  { Id: 4, first_name: "Мария", last_name: "Духонина", spec_name: "Тренер Теннис", department: {}, services: [
    { service_id: 4001, service_name: "Урок тенниса", service_price: 3500, duration: 60, is_group: 0 },
    { service_id: 4002, service_name: "Групповой урок тенниса", service_price: 1800, duration: 60, is_group: 1 }
  ]},
  { Id: 5, first_name: "Владислав", last_name: "Пресняков", spec_name: "Тренер Теннис", department: {}, services: [
    { service_id: 5001, service_name: "Урок тенниса", service_price: 3500, duration: 60, is_group: 0 }
  ]},
  { Id: 6, first_name: "Сергей", last_name: "Муравицкий", spec_name: "Тренер Теннис", department: {}, services: [
    { service_id: 6001, service_name: "Урок тенниса", service_price: 3500, duration: 60, is_group: 0 }
  ]}
];

export function BookingPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [selectedService, setSelectedService] = useState<any>(null);

  // Generate free time for selected employee and date with realistic scheduling
  const generateFreeTime = (empId: number, date: string) => {
    const times = [];
    const today = new Date();
    const selectedDateObj = new Date(date);
    const isToday = selectedDateObj.toDateString() === today.toDateString();
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    
    // Realistic schedule based on employee specialization
    const employee = realEmployees.find(emp => emp.Id === empId);
    const isTrainer = employee?.spec_name?.includes('Тренер');
    const isBeautySpecialist = employee?.spec_name?.includes('Визажист') || employee?.spec_name?.includes('бровист');
    
    // Different working hours for different specialists
    let startHour = 9;
    let endHour = 20;
    
    if (isBeautySpecialist) {
      startHour = 10;
      endHour = 19;
    }
    
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute of [0, 30]) {
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        // Skip past times for today
        if (isToday) {
          if (hour < currentHour || (hour === currentHour && minute <= currentMinute)) {
            continue;
          }
        }
        
        // Simulate realistic availability patterns
        let availabilityChance = 0.75; // Base 75% availability
        
        // Less availability during lunch hours (12-14)
        if (hour >= 12 && hour < 14) {
          availabilityChance = 0.3;
        }
        
        // Peak hours (18-20) are busier
        if (hour >= 18 && hour <= 20) {
          availabilityChance = 0.4;
        }
        
        // Beauty specialists have different patterns
        if (isBeautySpecialist) {
          // More availability in morning and afternoon
          if (hour >= 10 && hour < 12) availabilityChance = 0.8;
          if (hour >= 14 && hour < 17) availabilityChance = 0.85;
        }
        
        // Create some consistency - use employee ID and date as seed
        const seed = (empId * 100) + hour + minute + selectedDateObj.getDate();
        const pseudoRandom = ((seed * 9301 + 49297) % 233280) / 233280;
        
        if (pseudoRandom < availabilityChance) {
          times.push(timeSlot);
        }
      }
    }
    
    return times;
  };

  const availableTimes = selectedEmployee && selectedDate 
    ? generateFreeTime(selectedEmployee, selectedDate)
    : [];

  // Get service from URL parameter
  useEffect(() => {
    if (serviceId) {
      // Find service in employees
      for (const emp of realEmployees) {
        const service = emp.services.find(s => s.service_id === parseInt(serviceId));
        if (service) {
          setSelectedService({ ...service, employee: emp });
          break;
        }
      }
    }
  }, [serviceId]);

  // Generate available dates (next 14 days)
  const availableDates = Array.from({ length: 14 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMMM, EEEE', { locale: ru }),
      isToday: i === 0,
    };
  });

  const handleBooking = async () => {
    if (!selectedService || !selectedDate || !selectedTime || !selectedEmployee) return;
    
    try {
      // Simulate booking
      alert(`Запись создана!\nУслуга: ${selectedService.service_name}\nМастер: ${selectedService.employee.first_name} ${selectedService.employee.last_name}\nДата: ${selectedDate}\nВремя: ${selectedTime}\nСтоимость: ${selectedService.service_price}₽`);
      navigate('/profile');
    } catch (error) {
      console.error('Booking error:', error);
    }
  };

  if (!selectedService) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Услуга не найдена</h1>
          <button 
            onClick={() => navigate('/services')}
            className="bg-emerald-600 text-white px-6 py-3 rounded-lg"
          >
            Выбрать услугу
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/services')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Назад к услугам
          </button>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{selectedService.service_name}</h1>
          <p className="text-gray-600">
            Мастер: {selectedService.employee.first_name} {selectedService.employee.last_name} • 
            {selectedService.duration} мин • {selectedService.service_price}₽
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center mb-8">
          {[1, 2, 3, 4].map((stepNum) => (
            <div key={stepNum} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= stepNum ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {stepNum}
              </div>
              {stepNum < 4 && (
                <div className={`w-12 h-1 ${step > stepNum ? 'bg-emerald-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Date Selection */}
        {step === 1 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">1. Выберите дату</h2>
            <div className="grid grid-cols-2 gap-3">
              {availableDates.slice(0, 10).map((date) => (
                <button
                  key={date.value}
                  onClick={() => {
                    setSelectedDate(date.value);
                    setSelectedEmployee(selectedService.employee.Id);
                    setStep(2);
                  }}
                  className={`p-4 border rounded-lg text-left hover:border-emerald-500 transition-colors text-gray-900 ${
                    selectedDate === date.value ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                  }`}
                >
                  <div className="font-medium text-gray-900">{date.label}</div>
                  {date.isToday && <div className="text-sm text-emerald-600">Сегодня</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Time Selection */}
        {step === 2 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              2. Выберите время на {format(new Date(selectedDate), 'dd MMMM', { locale: ru })}
            </h2>
            
            {availableTimes.length > 0 ? (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {availableTimes.map((time) => (
                  <button
                    key={time}
                    onClick={() => {
                      setSelectedTime(time);
                      setStep(3);
                    }}
                    className={`p-3 border rounded-lg text-center hover:border-emerald-500 transition-colors ${
                      selectedTime === time ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">На выбранную дату нет свободного времени</p>
                <button
                  onClick={() => setStep(1)}
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  Выбрать другую дату
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">3. Подтверждение записи</h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Услуга:</span>
                <span className="font-medium text-gray-900">{selectedService.service_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Мастер:</span>
                <span className="font-medium text-gray-900">{selectedService.employee.first_name} {selectedService.employee.last_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Дата:</span>
                <span className="font-medium text-gray-900">{format(new Date(selectedDate), 'dd MMMM yyyy', { locale: ru })}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Время:</span>
                <span className="font-medium text-gray-900">{selectedTime}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Длительность:</span>
                <span className="font-medium text-gray-900">{selectedService.duration} мин</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Стоимость:</span>
                <span className="font-bold text-emerald-600 text-lg">{selectedService.service_price}₽</span>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setStep(2)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Назад
              </button>
              <button
                onClick={handleBooking}
                className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Подтвердить запись
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}